import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition, Switch } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Text } from '../layouts/fields/Text';
import { ListExtra } from '../layouts/fields/ListExtra';
import { privacyTypes2 } from '../../assets/scripts/variables';

export default function PlaylistModal({ open, setOpen, videoId, token }) {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [privacy, setPrivacy] = useState(privacyTypes2[0].title);
  const [privacyVal, setPrivacyVal] = useState(privacyTypes2[0]);
  const [create, setCreate] = useState(false);
  const [update, setUpdate] = useState(false);
  const [updatedPlaylist, setUpdatedPlaylist] = useState('');

  async function getPlaylists() {
    const req = await fetch(`${process.env.REACT_APP_API_URL}user/playlists`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Request-Method': 'POST',
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
        'Application-Key': `${process.env.REACT_APP_API}`,
        'X-TOKEN': `${token}`
      },
      body: JSON.stringify({ token })
    });
    const res = await req.json();
    setPlaylists(res.data);
    setLoading(false);
  }

  async function insertPlaylist(playlistId, videoId, playlistName) {
    setUpdatedPlaylist(playlistName);
    setUpdate(true);
    const req = await fetch(`${process.env.REACT_APP_API_URL}user/playlist/insert`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Application-Key': `${process.env.REACT_APP_API}`
      },
      body: JSON.stringify({ token, playlist: playlistId, video: videoId })
    });
    const res = await req.json();

    setTimeout(() => {
      setUpdate(false);
    }, 4000);
  }

  async function createPlaylist() {
    const req = await fetch(`${process.env.REACT_APP_API_URL}user/playlist/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Application-Key': `${process.env.REACT_APP_API}`
      },
      body: JSON.stringify({
        token,
        video: videoId,
        title,
        visibility: privacy
      })
    });
    const res = await req.json();
    setOpen(false);
    //Show modal to confirm list has been created
  }

  const isInPlaylist = (video, playlist) => {
    try {
      if (playlist.split(',').includes(video.toString())) {
        return true;
      }
      return false;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  useEffect(() => {
    getPlaylists();
    setCreate(false);
  }, [open]);

  return (
    <>
      {!loading && (
        <Transition.Root show={open} as={Fragment}>
          <Dialog as="div" className="relative z-10" onClose={setOpen}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0">
              <div className="fixed inset-0 bg-neutral-900 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                  <Dialog.Panel className="relative transform w-full rounded-lg bg-neutral-900 px-8 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full max-w-sm sm:p-6">
                    <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                      <button
                        type="button"
                        className="rounded-md text-white hover:text-gray-200 focus:outline-none"
                        onClick={() => setOpen(false)}>
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-8 w-8" aria-hidden="true" />
                      </button>
                    </div>
                    <div className="sm:flex sm:items-start">
                      <div className="mt-3 sm:mt-0 text-left">
                        <Dialog.Title
                          as="h3"
                          className="text-lg mb-4 font-semibold leading-6 text-white">
                          Save video to...
                        </Dialog.Title>
                      </div>
                    </div>
                    <div className="flex flex-col space-y-2 text-white">
                      {playlists.map((item) => (
                        <div
                          key={item.id}
                          className="flex flex-col text-sm transition bg-neutral-800 px-2 py-2 rounded-md cursor-pointer hover:bg-red-900">
                          <label className="cursor-pointer" htmlFor={item.id}>
                            <input
                              className="mr-2 text-red-500 focus:ring-red-500"
                              type="checkbox"
                              id={item.id}
                              onClick={() => insertPlaylist(item.id, videoId, item.playlist_title)}
                              name={item.id}
                              value={item.playlist_title}
                              defaultChecked={isInPlaylist(videoId, item.videos_payload)}
                            />
                            {item.playlist_title}
                          </label>
                        </div>
                      ))}

                      {update && (
                        <p className="my-2 text-xs rounded-md text-white bg-red-800 py-3 px-2">
                          "{updatedPlaylist}" has been updated
                        </p>
                      )}
                    </div>
                    <div className="mt-5 sm:mt-4 flex flex-col items-end">
                      {!create && (
                        <button
                          type="button"
                          className="inline-flex px-3 py-2 text-white text-sm font-semibold shadow-sm"
                          onClick={() => setCreate(!create)}>
                          Create Playlist
                        </button>
                      )}

                      {create && (
                        <div className="grid grid-cols-1 w-full text-white">
                          <Text type="text" label="Title" value={title} setValue={setTitle} />
                          <div className="mt-4">
                            <ListExtra
                              title="Privacy"
                              val={privacyVal}
                              arr={privacyTypes2}
                              func1={setPrivacy}
                              func2={setPrivacyVal}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => createPlaylist()}
                            className="text-md font-semibold text-right">
                            Create
                          </button>
                        </div>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      )}
    </>
  );
}
